/**
 * 원문 가림의 사용자 설정 — 호스트·뷰어 **독립**으로 기억한다.
 *
 * 저장소는 localStorage 다(세션이 아니라 이 브라우저의 취향이므로 —
 * use-feed-order 와 같은 규율). 프라이빗 모드 등으로 저장이 막혀도 화면은
 * 정상 동작해야 한다 — 실패는 조용히 무시하고 메모리 상태로만 산다.
 */

import { useCallback, useEffect, useState } from 'react'
import { parseVeil, veilStorageKey, type VeilRole } from './source-veil'

export function useSourceVeil(role: VeilRole): {
  veiled: boolean
  setVeiled: (veiled: boolean) => void
  toggle: () => void
} {
  const [veiled, setVeiledState] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false
    try {
      return parseVeil(localStorage.getItem(veilStorageKey(role)))
    } catch {
      return false
    }
  })

  /**
   * 다른 탭의 토글을 따라간다(QA P3) — feed-order 는 동기화하지 않지만
   * 가림은 판돈이 다르다: 프로젝터에 띄워 둔 낡은 탭이 원문을 그대로
   * 보여 주는 동안 사용자는 다른 탭에서 "가렸다" 고 믿는, 배려가 무너지는
   * 바로 그 시나리오다. storage 이벤트는 **다른 문서**의 쓰기에만 오므로
   * 자기 토글과 겹치지 않는다.
   */
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key === veilStorageKey(role)) setVeiledState(parseVeil(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [role])

  const setVeiled = useCallback(
    (next: boolean) => {
      setVeiledState(next)
      try {
        localStorage.setItem(veilStorageKey(role), next ? '1' : '0')
      } catch {
        // 저장 실패는 무시한다 — 가림은 이번 세션만 유지된다.
      }
    },
    [role],
  )

  const toggle = useCallback(() => {
    setVeiled(!veiled)
  }, [veiled, setVeiled])

  return { veiled, setVeiled, toggle }
}
